import './style.scss';

import React, { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { components } from 'react-select';
import Creatable from 'react-select/creatable';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectXRayOperators,
  selectXRaySelectedOperators,
} from 'app/redux/xrayGeneratorConfiguration/reducer';
import {
  addOperator,
  deleteOperator,
  setSelectedOperators,
} from 'app/redux/xrayGeneratorConfiguration/actions';
import _ from 'lodash';
import { DARK_INPUT_STYLE } from 'app/ReactSelectStyles';
import { Icon, Modal } from 'semantic-ui-react';
import ConfirmWithIntl from 'app/components/ConfirmWithIntl';
import preventDefault from 'app/utils/preventDefault';

const operatorListToOptions = (operator) => ({
  label: operator,
  value: operator,
});

function OperatorDeleteButton({ operator, children }) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };
  const handleConfirm = () => {
    dispatch(deleteOperator(operator));
    setOpen(false);
  };
  const handleCancel = () => setOpen(false);

  return (
    <div className="operator-option">
      {children}
      <button type="button" onClick={handleButtonClick} className="operator-option__delete-button">
        <Icon name="trash" />
      </button>
      {/* The div with display none is a workaround to be able to cancel/close the modal without selecting the operator */}
      <div style={{ display: 'none' }} onClick={preventDefault} onMouseDown={preventDefault}>
        <ConfirmWithIntl
          open={open}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          headerId="xray_generator.operator.deleteOperator.header"
          content={
            <Modal.Content>
              <FormattedMessage
                id="xray_generator.operator.deleteOperator.text"
                values={{ operator }}
              />
            </Modal.Content>
          }
          dangerous
        />
      </div>
    </div>
  );
}

function OptionWithDelete(props) {
  if (props.data.__isNew__) {
    return <components.Option {...props} />;
  }
  return (
    <components.Option {...props}>
      <OperatorDeleteButton operator={props.data.value}>{props.children}</OperatorDeleteButton>
    </components.Option>
  );
}

function XRayOperatorSelector() {
  const intl = useIntl();
  const operators = useSelector(selectXRayOperators);
  const selectedOperators = useSelector(selectXRaySelectedOperators);
  const dispatch = useDispatch();

  const operatorsOptions = useMemo(() => operators.map(operatorListToOptions) ?? [], [operators]);
  const selectedOperatorsOptions = useMemo(
    () => selectedOperators.map(operatorListToOptions) ?? [],
    [selectedOperators]
  );
  const onCreateOperator = useCallback(
    (operator) => {
      dispatch(addOperator(operator));
      dispatch(setSelectedOperators([...(selectedOperators ?? []), operator]));
    },
    [dispatch, selectedOperators]
  );

  return (
    <div className="xray-operators">
      <h3 className="xray-operators__header">
        <FormattedMessage id="xray_generator.operators" />:
      </h3>
      <div className="xray-operators__dropdown">
        <Creatable
          isMulti
          onChange={(value) => dispatch(setSelectedOperators(_.map(value, 'value')))}
          options={operatorsOptions}
          value={selectedOperatorsOptions}
          onCreateOption={onCreateOperator}
          styles={DARK_INPUT_STYLE}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
          }
          components={{ Option: OptionWithDelete }}
        />
      </div>
    </div>
  );
}

export default memo(XRayOperatorSelector);
